import React, { useState, useEffect } from "react";
import { ArrowBigDown, Menu, X, Users, Calendar, CheckCircle2, BookOpen, Shield } from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import { Link, useLocation, useNavigate } from "react-router-dom";

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isHomePage = location.pathname === "/";

  const scrollToSection = (sectionId: string) => {
    if (!isHomePage) {
      navigate('/', { state: { scrollTo: sectionId } });
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  useEffect(() => {
    if (location.state?.scrollTo) {
      const sectionId = location.state.scrollTo;
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        window.history.replaceState({}, document.title);
      }
    }
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white bg-opacity-90 dark:bg-gray-900 dark:bg-opacity-90 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center space-x-2">
            <ArrowBigDown className="text-primary w-8 h-8" strokeWidth={2.5} />
            <span className="font-medium text-xl tracking-tight">
              Arrow Drop Foundation
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('about')}
              className="text-foreground hover:text-primary transition-colors"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('mission')}
              className="text-foreground hover:text-primary transition-colors"
            >
              Mission
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-foreground hover:text-primary transition-colors"
            >
              Contact
            </button>
            <Link
              to="/volunteers"
              className="text-foreground hover:text-primary transition-colors flex items-center"
            >
              <Users className="w-4 h-4 mr-1" />
              Volunteers
            </Link>
            <Link
              to="/events"
              className="text-foreground hover:text-primary transition-colors flex items-center"
            >
              <Calendar className="w-4 h-4 mr-1" />
              Events
            </Link>
            <Link
              to="/resources"
              className="text-foreground hover:text-primary transition-colors flex items-center"
            >
              <BookOpen className="w-4 h-4 mr-1" />
              Resources
            </Link>
            <Link
              to="/transparency"
              className="text-foreground hover:text-primary transition-colors flex items-center"
            >
              <Shield className="w-4 h-4 mr-1" />
              Transparency
            </Link>
            <Link
              to="/verify"
              className="text-foreground hover:text-primary transition-colors flex items-center"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Verify
            </Link>
            <ThemeToggle />
          </nav>

          {/* Mobile Nav Toggle */}
          <div className="flex items-center md:hidden">
            <ThemeToggle />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="ml-2 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-background border-t border-border animate-fade-in">
          <div className="container mx-auto px-4 py-4">
            <nav className="flex flex-col space-y-4">
              <button
                onClick={() => scrollToSection('about')}
                className="py-2 text-foreground hover:text-primary transition-colors text-left"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('mission')}
                className="py-2 text-foreground hover:text-primary transition-colors text-left"
              >
                Mission
              </button>
              <button
                onClick={() => scrollToSection('roadmap')}
                className="py-2 text-foreground hover:text-primary transition-colors text-left"
              >
                Roadmap
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="py-2 text-foreground hover:text-primary transition-colors text-left"
              >
                Contact
              </button>
              <Link
                to="/volunteers"
                className="py-2 text-foreground hover:text-primary transition-colors flex items-center"
                onClick={() => setIsMenuOpen(false)}
              >
                <Users className="w-4 h-4 mr-1" />
                Volunteers
              </Link>
              <Link
                to="/events"
                className="py-2 text-foreground hover:text-primary transition-colors flex items-center"
                onClick={() => setIsMenuOpen(false)}
              >
                <Calendar className="w-4 h-4 mr-1" />
                Events
              </Link>
              <Link
                to="/resources"
                className="py-2 text-foreground hover:text-primary transition-colors flex items-center"
                onClick={() => setIsMenuOpen(false)}
              >
                <BookOpen className="w-4 h-4 mr-1" />
                Resources
              </Link>
              <Link
                to="/transparency"
                className="py-2 text-foreground hover:text-primary transition-colors flex items-center"
                onClick={() => setIsMenuOpen(false)}
              >
                <Shield className="w-4 h-4 mr-1" />
                Transparency
              </Link>
              <Link
                to="/verify"
                className="py-2 text-foreground hover:text-primary transition-colors flex items-center"
                onClick={() => setIsMenuOpen(false)}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Verify
              </Link>
              <Link
                to="/privacy-policy"
                className="py-2 text-foreground hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Privacy Policy
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;